import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CustomNumberInput from "components/CustomNumberInput";
import AddLocationModal from "components/lists/locations/AddLocationModal";
import { LocationTypes, STATUS_CODES } from "constants/index";
import { ENTITY } from "constants/entities";
import { LOCATIONS_URL } from "constants/urls";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useMemo, useState } from "react";
import { MdDelete } from "react-icons/md";

import {
  ArticleLocation,
  Location,
  LocationBase,
  Store,
  Transfer,
} from "types";

type Props = {
  stores: Store[];
  locations: Location[];
  articleLocations: ArticleLocation[];
  handleChange: (
    name: string,
    value: string | number | Transfer | Transfer[] | null,
  ) => void;
  storeId?: number | null;
  transfer?: Transfer;
  transfers?: Transfer[];
  selectedOrigins?: ArticleLocation[];
};

function TransferNewLocation({
  stores,
  locations,
  articleLocations,
  handleChange,
  storeId,
  transfer,
  transfers = [],
  selectedOrigins = [],
}: Props) {
  const { showSnackBar } = useToast();
  const [addNewLocationModalOpen, setAddNewLocationModalOpen] =
    useState<boolean>(false);
  const { createData: createLocation, loading: locationLoading } =
    useCrud<LocationBase>(LOCATIONS_URL, {
      disableAutoFetch: true,
    });
  const { addSessionData } = useSession();

  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const [selectedOrigin, setSelectedOrigin] = useState<ArticleLocation | null>(
    transfer?.from ?? null,
  );
  const [destinationAmount, setDestinationAmount] = useState<number>(1);
  const [formResetKey, setFormResetKey] = useState<number>(0);

  useEffect(() => {
    if (!transfer?.from) {
      setSelectedOrigin(null);
      return;
    }

    const selectedOriginStillAvailable = selectedOrigins.some(
      (origin) => origin.id === transfer.from?.id,
    );

    setSelectedOrigin(selectedOriginStillAvailable ? transfer.from : null);
  }, [selectedOrigins, transfer?.from]);

  const selectedStore = useMemo(() => {
    if (storeId === 0) {
      return { id: 0, label: "Todos" };
    }

    const store = stores.find((s) => s.id === storeId);
    return store ? { id: store.id, label: store.description } : null;
  }, [storeId, stores]);

  const availableLocations = useMemo(() => {
    let newLocations = locations.filter(
      (l) =>
        (storeId === 0 || l.store_id === storeId) &&
        l.id !== selectedOrigin?.location_id,
    );

    newLocations = newLocations.concat({
      code: "Nueva Ubicación",
      store_id: storeId ?? 0,
      width: 0,
      height: 0,
      long: 0,
      occupied: 0,
      id: 0,
      store: {
        id: storeId ?? 0,
        code: "",
        description: "",
        is_active: true,
      },
      location_type: LocationTypes.PISO,
    });

    return newLocations;
  }, [locations, selectedOrigin?.location_id, storeId]);

  const listedTransfers = transfers;
  const stockQuantity = Number(selectedOrigin?.stock ?? 0);
  const destinationsTotal = listedTransfers
    .filter((item) => item.from?.id === selectedOrigin?.id)
    .reduce((sum, item) => sum + Number(item.amount ?? 0), 0);
  const remainingStock = Math.max(0, stockQuantity - destinationsTotal);
  const isDestinationAmountOverStock =
    Boolean(selectedOrigin) && destinationAmount > remainingStock;

  const originOptions = useMemo(
    () => selectedOrigins,
    [selectedOrigins],
  );

  const getOriginLabel = (origin: ArticleLocation) => {
    const article = origin.article;
    const location = origin.location;
    const code = article?.code ?? origin.article_id;
    const description = article?.description ?? "";
    const locationCode = location?.code ?? origin.location_id;

    return `${locationCode} - ${code} - ${description}`;
  };

  const syncTransfers = (nextTransfers: Transfer[]) => {
    handleChange("transfers", nextTransfers);
  };

  const resetDestinationForm = () => {
    setSelectedOrigin(null);
    setSelectedLocation(null);
    setDestinationAmount(1);
    setFormResetKey((prev) => prev + 1);
    handleChange("store_id", 0);
    handleChange("transfer", {
      from: null,
      to: null,
      amount: 0,
      new_location_id: null,
      destinations: [],
      stock_unit_ids: undefined,
    });
  };

  const handleAddDestination = () => {
    if (
      !selectedOrigin ||
      !selectedLocation?.id ||
      destinationAmount <= 0 ||
      isDestinationAmountOverStock
    ) {
      return;
    }

    const nextTransfers = [
      ...listedTransfers,
      {
        from: selectedOrigin,
        to: null,
        amount: destinationAmount,
        new_location_id: null,
        destinations: [
          {
            store_id: selectedLocation.store_id,
            location_id: selectedLocation.id,
            amount: destinationAmount,
          },
        ],
      },
    ];

    syncTransfers(nextTransfers);
    resetDestinationForm();
  };

  const handleRemoveDestination = (indexToRemove: number) => {
    const nextTransfers = listedTransfers.filter(
      (_, index) => index !== indexToRemove,
    );

    syncTransfers(nextTransfers);
  };

  const handleCreateLocation = async (newLocation: LocationBase) => {
    const response = await createLocation(newLocation, true, true);

    if (response.status === STATUS_CODES.OK) {
      const created = response.data;

      setSelectedLocation(created);
      addSessionData(ENTITY.LOCATIONS, created);

      if (!storeId || storeId === 0) {
        handleChange("store_id", created.store_id);
      }

      if (selectedOrigin) {
        const nextTransfers = [
          ...listedTransfers,
          {
            from: selectedOrigin,
            to: null,
            amount: destinationAmount,
            new_location_id: null,
            destinations: [
              {
                store_id: created.store_id,
                location_id: created.id,
                amount: destinationAmount,
              },
            ],
          },
        ];

        syncTransfers(nextTransfers);
      }

      resetDestinationForm();

      setAddNewLocationModalOpen(false);
      showSnackBar("Se ha agregado la ubicación correctamente.", "success");
    } else {
      showSnackBar(
        "No se ha podido agregar la ubicación. Por favor intente nuevamente.",
        "error",
      );
    }
  };

  return (
    <>
      <Box width="100%" pt={5}>
        <Typography variant="h6">Hacia</Typography>
      </Box>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box p={2}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={false}
              sx={{ flexBasis: { md: "55%" }, maxWidth: { md: "55%" } }}
            >
              <FormControl fullWidth margin="none">
                <Autocomplete
                  key={`origin-${formResetKey}`}
                  autoComplete
                  disabled={originOptions.length === 0}
                  options={originOptions}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={getOriginLabel}
                  value={selectedOrigin}
                  onChange={(_, value: ArticleLocation | null) => {
                    setSelectedOrigin(value);
                    setSelectedLocation(null);
                    setDestinationAmount(1);
                    handleChange("transfer", {
                      from: value,
                      to: null,
                      amount: 0,
                      new_location_id: null,
                      destinations: [],
                      stock_unit_ids: undefined,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} required label="Artículo origen (Ubi/Cod/Desc)" />
                  )}
                />
              </FormControl>
              {selectedOrigin && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Stock: {stockQuantity} | Agregado: {destinationsTotal} |
                  Disponible: {remainingStock}
                </Typography>
              )}
            </Grid>
          <Grid
            item
            xs={12}
            md={false}
            sx={{ flexBasis: { md: "15%" }, maxWidth: { md: "15%" } }}
          >
            <FormControl fullWidth margin="none">
                <Autocomplete
                key={`warehouse-${formResetKey}`}
                id="warehouse"
                autoComplete
                disabled={!selectedOrigin}
                options={[
                  { id: 0, label: "Todos" },
                  ...stores
                    .filter((s) => s.is_active)
                    .map((s) => ({
                      id: s.id,
                      label: s.description,
                    })),
                ]}
                value={selectedStore}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => {
                  handleChange("store_id", value?.id ?? null);
                }}
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Almacen destino" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            md={false}
            sx={{ flexBasis: { md: "15%" }, maxWidth: { md: "15%" } }}
          >
            {locationLoading ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth margin="none">
                <Autocomplete
                  key={`destination-${formResetKey}`}
                  autoComplete
                  disableClearable
                  disabled={
                    !selectedOrigin ||
                    storeId == null ||
                    availableLocations.length === 0
                  }
                  options={availableLocations}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) => option.code}
                  value={selectedLocation || undefined}
                  onChange={(e, value: Location | null) => {
                    if (value && !value.id) {
                      setAddNewLocationModalOpen(true);
                      return;
                    }

                    setSelectedLocation(value);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ubicación destino" />
                  )}
                />
              </FormControl>
            )}
          </Grid>
          <Grid
            item
            xs={12}
            md={false}
            sx={{ flexBasis: { md: "15%" }, maxWidth: { md: "15%" } }}
          >
            <Box display="flex" gap={1} alignItems="flex-start">
              <Box flex={1}>
                <CustomNumberInput
                  label="Cantidad"
                  disabled={!selectedOrigin}
                  value={destinationAmount}
                  onChange={(e) =>
                    setDestinationAmount(parseInt(e.target.value, 10) || 0)
                  }
                />
              </Box>
              <Button
                type="button"
                variant="contained"
                onClick={handleAddDestination}
                disabled={
                  !selectedOrigin ||
                  !selectedLocation?.id ||
                  destinationAmount <= 0 ||
                  isDestinationAmountOverStock
                }
                sx={{ height: 46, minWidth: 48, mt: "10px" }}
              >
                +
              </Button>
            </Box>
          </Grid>
          {isDestinationAmountOverStock && (
            <Grid item xs={12}>
              <Alert severity="warning">
                La suma de cantidades no puede superar el stock disponible.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artículo origen (Ubi/Cod/Desc)</TableCell>
                    <TableCell>Almacén destino</TableCell>
                    <TableCell>Ubicación destino</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listedTransfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No hay transferencias para mostrar.
                      </TableCell>
                    </TableRow>
                  ) : (
                    listedTransfers.map((listedTransfer, index) => {
                      const destination = listedTransfer.destinations?.[0];
                      const origin = listedTransfer.from;
                      const location = locations.find(
                        (item) => item.id === destination?.location_id,
                      );
                      const store = stores.find(
                        (item) => item.id === destination?.store_id,
                      );

                      return (
                        <TableRow
                          key={`${origin?.id ?? "origin"}-${
                            destination?.location_id ?? "destination"
                          }-${index}`}
                        >
                          <TableCell>{origin ? getOriginLabel(origin) : "-"}</TableCell>
                          <TableCell>{store?.description ?? "-"}</TableCell>
                          <TableCell>{location?.code ?? "-"}</TableCell>
                          <TableCell align="right">{listedTransfer.amount}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleRemoveDestination(index)}
                            >
                              <MdDelete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            {locations.filter(
              (l) => !articleLocations.some((al) => al.location_id === l.id),
            ).length === 0 && (
              <Alert severity="warning" sx={{ height: "100%" }}>
                No hay ubicaciones vacias disponibles en el almacen
                seleccionado.
              </Alert>
            )}
          </Grid>
        </Grid>
        <AddLocationModal
          open={addNewLocationModalOpen}
          handleOpen={() => {
            setSelectedLocation(null);
            setAddNewLocationModalOpen(false);
          }}
          onCreate={handleCreateLocation}
          stores={
            storeId && storeId !== 0
              ? stores.filter((store) => store.id === storeId)
              : stores
          }
        />
      </Box>
    </Paper>
    </>
  );
}

export default React.memo(TransferNewLocation);
